import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { getFirestore, collection, getDocs, getDoc, doc, updateDoc, arrayUnion, Timestamp } from '@firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app } from '../firebase.js';
import { useNavigate } from 'react-router-dom';
import defaulProfPic from '../Images/defprofile.png';
import MaskedPhone from '../Components/MaskedPhone';
import { logAdminActivity } from '../utils/activityLogger';
import { buildSecondaryCourseEmail } from '../utils/emailTemplates';
import swal from 'sweetalert';

const normalizeSecondaryCourses = (secondaryCourses = []) => {
  if (!Array.isArray(secondaryCourses)) return [];

  return secondaryCourses
    .map((entry) => {
      if (typeof entry === 'string') {
        return {
          name: entry,
          intakeId: '',
          intakeDate: '',
          rawIntakeDate: '',
          campus: '',
          status: 'Pending',
          admissionDate: '',
          addedAt: ''
        };
      }

      return {
        name: entry?.name || entry?.courseName || '',
        intakeId: entry?.intakeId || '',
        intakeDate: entry?.intakeDate || entry?.intake || '',
        rawIntakeDate: entry?.rawIntakeDate || '',
        campus: entry?.campus || '',
        status: entry?.status || 'Pending',
        admissionDate: entry?.admissionDate || '',
        addedAt: entry?.addedAt || entry?.createdAt || ''
      };
    })
    .filter((entry) => entry.name);
};

const toTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const getSecondaryCourseSortValue = (data = {}) => {
  const secondaryCourse = normalizeSecondaryCourses([
    data.pendingSecondaryCourse,
    ...(Array.isArray(data.secondaryCourses) ? data.secondaryCourses : [])
  ].filter(Boolean))[0];

  return Math.max(
    toTimestampMs(secondaryCourse?.addedAt),
    toTimestampMs(secondaryCourse?.admissionDate),
    toTimestampMs(data.appDate),
    toTimestampMs(data.updatedAt),
    toTimestampMs(data.createdAt)
  );
};

const formatDateTime = (value) => {
  const timestamp = toTimestampMs(value);
  if (!timestamp) return '—';

  return new Date(timestamp).toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const buildSearchBlob = (student = {}, course = {}) => [
  student.bio,
  student.userId,
  student.course,
  student.userEmail,
  student.phoneNumber,
  student.userStatus,
  student.nationality,
  student.cohort,
  course.name,
  course.status,
  course.intakeId,
  course.intakeDate,
  course.rawIntakeDate,
  course.campus,
  course.admissionDate,
  course.addedAt
]
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

const normalizeCourseName = (value) => String(value || '').trim().toLowerCase();

const normalizeCampus = (value) => String(value || '').trim().toLowerCase();

const formatIntakeLabel = (intake) => {
  if (!intake) return 'Not set';
  if (typeof intake === 'string') return intake;
  if (intake.intakeDate && intake.campus) return `${intake.intakeDate} • ${intake.campus}`;
  return intake.intakeDate || intake.rawIntakeDate || intake.campus || 'Not set';
};

const formatPhoneNumber = (number) => {
  if (!number) return null;
  let cleaned = String(number).replace(/[^0-9]/g, '');
  if (cleaned.startsWith('0')) {
    cleaned = `254${cleaned.slice(1)}`;
  } else if (cleaned.startsWith('7') || cleaned.startsWith('1')) {
    cleaned = `254${cleaned}`;
  }
  return cleaned.length === 12 && cleaned.startsWith('254') ? cleaned : cleaned || null;
};

function SecondaryCoursesComp() {
  const db = getFirestore(app);
  const auth = getAuth(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [studentsList, setStudentsList] = useState([]);
  const [allStudentOptions, setAllStudentOptions] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [allIntakes, setAllIntakes] = useState([]);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [selectedStudentDocId, setSelectedStudentDocId] = useState('');
  const [selectedStudentRecord, setSelectedStudentRecord] = useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [selectedSecondaryCourse, setSelectedSecondaryCourse] = useState('');
  const [selectedIntakeId, setSelectedIntakeId] = useState('');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [loadingStudentRecord, setLoadingStudentRecord] = useState(false);
  const [loadingStudentOptions, setLoadingStudentOptions] = useState(false);
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  const lastSearchRef = useRef('');

  const fetchData = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const studentRows = [];

      querySnapshot.docs
        .slice()
        .sort((left, right) => getSecondaryCourseSortValue(right.data()) - getSecondaryCourseSortValue(left.data()))
        .forEach((docSnap) => {
        const data = docSnap.data();
        const baseStudent = {
          studentDocId: docSnap.id,
          userId: data.userId || docSnap.id,
          bio: data.bio || data.userName || data.fullName || 'Unnamed student',
          profilePic: data.profilePic || defaulProfPic,
          course: data.course || '',
          userEmail: data.userEmail || '',
          phoneNumber: data.phoneNumber || '',
          userStatus: data.userStatus || data.Status || '',
          nationality: data.nationality || '',
          cohort: data.cohort || '',
          appDate: data.appDate || '',
          searchBlob: buildSearchBlob(data, {})
        };

        const secondaryCourses = normalizeSecondaryCourses(data.secondaryCourses);

        if (secondaryCourses.length === 0 && !data.pendingSecondaryCourse) {
          return;
        }

        const pendingSecondaryCourse = data.pendingSecondaryCourse
          ? normalizeSecondaryCourses([data.pendingSecondaryCourse])[0]
          : null;

        const normalizedCourses = pendingSecondaryCourse
          ? [...secondaryCourses, pendingSecondaryCourse]
          : secondaryCourses;
        const seenCourses = new Set();

        normalizedCourses.forEach((course, index) => {
          const courseKey = [
            course.name,
            course.intakeId,
            course.intakeDate,
            course.rawIntakeDate,
            course.campus,
            course.admissionDate,
            course.addedAt
          ].join('|');

          if (seenCourses.has(courseKey)) {
            return;
          }
          seenCourses.add(courseKey);

          studentRows.push({
            id: `${docSnap.id}-${index}`,
            ...baseStudent,
            secondaryCourse: course,
            searchBlob: buildSearchBlob(data, course)
          });
        });
        });

      setStudentsList(studentRows);
    } catch (error) {
      console.error('Error loading secondary course students:', error);
      setStudentsList([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  const fetchAllStudentOptions = useCallback(async () => {
    setLoadingStudentOptions(true);

    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const allStudentRows = [];

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        allStudentRows.push({
          studentDocId: docSnap.id,
          userId: data.userId || docSnap.id,
          bio: data.bio || data.userName || data.fullName || 'Unnamed student',
          profilePic: data.profilePic || defaulProfPic,
          course: data.course || '',
          userEmail: data.userEmail || '',
          phoneNumber: data.phoneNumber || '',
          userStatus: data.userStatus || data.Status || '',
          nationality: data.nationality || '',
          cohort: data.cohort || '',
          appDate: data.appDate || '',
          searchBlob: buildSearchBlob(data, {})
        });
      });

      setAllStudentOptions(allStudentRows);
    } catch (error) {
      console.error('Error loading modal student search options:', error);
      setAllStudentOptions([]);
    } finally {
      setLoadingStudentOptions(false);
    }
  }, [db]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCatalogData = useCallback(async () => {
    try {
      const [coursesSnapshot, intakeSnapshot] = await Promise.all([
        getDocs(collection(db, 'courses')),
        getDocs(collection(db, 'Intakes'))
      ]);

      const courseList = [];
      coursesSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const name = data.mcourseName || data.courseName || data.name || '';
        if (!name) return;
        courseList.push({
          id: docSnap.id,
          name,
          description: data.mcourseDescription || data.courseDescription || data.description || '',
          duration: data.mcourseDuration || data.courseDuration || ''
        });
      });

      const intakeList = [];
      intakeSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        intakeList.push({
          id: data.docID || docSnap.id,
          courseName: data.CourseName || data.courseName || '',
          intakeDate: data.intakeDate || '',
          rawIntakeDate: data.rawIntakeDate || '',
          campus: data.Campus || data.campus || ''
        });
      });

      setAllCourses(courseList);
      setAllIntakes(intakeList);
    } catch (error) {
      console.error('Error loading secondary course catalog:', error);
      setAllCourses([]);
      setAllIntakes([]);
    }
  }, [db]);

  useEffect(() => {
    fetchCatalogData();
  }, [fetchCatalogData]);

  useEffect(() => {
    fetchAllStudentOptions();
  }, [fetchAllStudentOptions]);

  const uniqueStudentOptions = useMemo(() => {
    const seen = new Set();
    return allStudentOptions.reduce((accumulator, student) => {
      if (!student?.studentDocId || seen.has(student.studentDocId)) {
        return accumulator;
      }

      seen.add(student.studentDocId);
      accumulator.push({
        studentDocId: student.studentDocId,
        userId: student.userId,
        bio: student.bio,
        course: student.course,
        userEmail: student.userEmail,
        phoneNumber: student.phoneNumber,
        profilePic: student.profilePic,
        userStatus: student.userStatus,
        nationality: student.nationality,
        cohort: student.cohort,
        searchBlob: student.searchBlob
      });
      return accumulator;
      }, []);
  }, [allStudentOptions]);

  const filteredStudentOptions = useMemo(() => {
    const normalizedSearch = studentSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return [];

    return uniqueStudentOptions.filter((student) => {
      return (student.searchBlob || '').includes(normalizedSearch);
    });
  }, [studentSearchTerm, uniqueStudentOptions]);

  const selectedStudentSecondaryCourses = useMemo(() => (
    normalizeSecondaryCourses([
      ...(Array.isArray(selectedStudentRecord?.secondaryCourses) ? selectedStudentRecord.secondaryCourses : []),
      selectedStudentRecord?.pendingSecondaryCourse
    ].filter(Boolean))
  ), [selectedStudentRecord]);

  const availableSecondaryCourses = useMemo(() => {
    if (!selectedStudentRecord) return [];
    const primaryCourse = normalizeCourseName(selectedStudentRecord.course);
    const alreadySelected = new Set(selectedStudentSecondaryCourses.map((course) => normalizeCourseName(course.name)));

    return allCourses.filter((course) => {
      const courseName = normalizeCourseName(course.name);
      if (!courseName) return false;
      if (courseName === primaryCourse) return false;
      return !alreadySelected.has(courseName);
    });
  }, [allCourses, selectedStudentRecord, selectedStudentSecondaryCourses]);

  const availableIntakes = useMemo(() => {
    if (!selectedSecondaryCourse) return [];
    const normalizedCourse = normalizeCourseName(selectedSecondaryCourse);
    return allIntakes
      .filter((intake) => normalizeCourseName(intake.courseName) === normalizedCourse)
      .sort((first, second) => String(first.rawIntakeDate || first.intakeDate || '').localeCompare(String(second.rawIntakeDate || second.intakeDate || '')));
  }, [allIntakes, selectedSecondaryCourse]);

  const selectedIntake = useMemo(() => (
    selectedIntakeId ? allIntakes.find((intake) => intake.id === selectedIntakeId) || null : null
  ), [allIntakes, selectedIntakeId]);

  const availableCampusOptions = useMemo(() => {
    if (!selectedIntake) return [];
    const campus = selectedIntake.campus || '';
    if (!campus) return [];
    if (normalizeCampus(campus) === 'general') {
      return ['CBD Campus', 'Virtual Classes'];
    }
    return [campus];
  }, [selectedIntake]);

  const campusRequiresChoice = useMemo(() => availableCampusOptions.length > 1, [availableCampusOptions]);

  useEffect(() => {
    if (!showAddCourseModal) {
      setSelectedStudentDocId('');
      setSelectedStudentRecord(null);
      setSelectedSecondaryCourse('');
      setSelectedIntakeId('');
      setSelectedCampus('');
      setLoadingStudentRecord(false);
      setStudentSearchTerm('');
      return;
    }
  }, [showAddCourseModal]);

  useEffect(() => {
    if (!selectedStudentDocId) {
      setSelectedStudentRecord(null);
      setSelectedSecondaryCourse('');
      setSelectedIntakeId('');
      setSelectedCampus('');
      return;
    }

    let cancelled = false;

    const loadSelectedStudent = async () => {
      setLoadingStudentRecord(true);
      try {
        const snapshot = await getDoc(doc(db, 'users', selectedStudentDocId));
        if (cancelled) return;
        setSelectedStudentRecord(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
        setSelectedSecondaryCourse('');
        setSelectedIntakeId('');
        setSelectedCampus('');
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading selected student profile:', error);
          setSelectedStudentRecord(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingStudentRecord(false);
        }
      }
    };

    loadSelectedStudent();

    return () => {
      cancelled = true;
    };
  }, [db, selectedStudentDocId]);

  useEffect(() => {
    setSelectedIntakeId('');
    setSelectedCampus('');
  }, [selectedSecondaryCourse]);

  useEffect(() => {
    if (!selectedIntakeId) {
      setSelectedCampus('');
      return;
    }
    if (availableCampusOptions.length === 1) {
      setSelectedCampus(availableCampusOptions[0]);
      return;
    }
    if (!availableCampusOptions.includes(selectedCampus)) {
      setSelectedCampus('');
    }
  }, [availableCampusOptions, selectedCampus, selectedIntakeId]);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timeoutId = setTimeout(() => {
      if (trimmed === lastSearchRef.current) return;
      lastSearchRef.current = trimmed;
      if (trimmed) {
        logAdminActivity({
          action: 'Search',
          details: `Secondary Courses search: "${trimmed}"`,
          meta: { page: 'Secondary Courses', query: trimmed }
        });
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const filteredStudents = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return studentsList;

    return studentsList.filter((student) => student.searchBlob.includes(normalized));
  }, [searchTerm, studentsList]);

  const uniqueStudentsCount = useMemo(
    () => new Set(studentsList.map((student) => student.userId).filter(Boolean)).size,
    [studentsList]
  );

  const uniqueSecondaryCourses = useMemo(
    () => new Set(studentsList.map((student) => student.secondaryCourse?.name).filter(Boolean)).size,
    [studentsList]
  );

  const viewStudent = useCallback((student) => {
    if (!student?.userId) return;
    logAdminActivity({
      action: 'View Student',
      details: `Opened secondary course student profile: ${student.bio || 'student'}`,
      meta: { page: 'Secondary Courses', studentId: student.userId, studentName: student.bio || '' }
    });
    navigate('/view-student', { state: { myData: student.userId, secondaryCourse: student.secondaryCourse } });
  }, [navigate]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchData(), fetchAllStudentOptions()]);
  }, [fetchAllStudentOptions, fetchData]);

  const openAddCourseModal = useCallback(() => {
    setStudentSearchTerm('');
    setShowAddCourseModal(true);
  }, []);

  const closeAddCourseModal = useCallback(() => {
    setShowAddCourseModal(false);
    setSelectedStudentDocId('');
    setSelectedStudentRecord(null);
    setStudentSearchTerm('');
    setSelectedSecondaryCourse('');
    setSelectedIntakeId('');
    setSelectedCampus('');
  }, []);

  const sendCourseAddedNotifications = useCallback(async (studentRecordValue, courseEntry) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const token = await currentUser.getIdToken();
    const formattedPhone = formatPhoneNumber(studentRecordValue?.phoneNumber);
    const intakeLabel = formatIntakeLabel({
      intakeDate: courseEntry?.intakeDate || '',
      rawIntakeDate: courseEntry?.rawIntakeDate || '',
      campus: courseEntry?.campus || ''
    });
    const studentName = studentRecordValue?.bio || studentRecordValue?.userName || studentRecordValue?.fullName || 'Student';
    const courseName = courseEntry?.name || 'Course';
    const emailSubject = `Secondary course added: ${courseName}`;
    const emailMessage = buildSecondaryCourseEmail({
      studentName,
      courseName,
      intakeLabel
    });

    if (studentRecordValue?.userEmail) {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: studentRecordValue.userEmail,
          message: emailMessage,
          subject: emailSubject
        })
      }).catch((error) => {
        console.error('Error sending secondary course email:', error);
      });
    }

    if (formattedPhone) {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          message: `Hi ${studentName}, your secondary course "${courseName}" has been added for intake ${intakeLabel}. Please pay your invoice to complete admission. - Ubunifu College`
        })
      }).catch((error) => {
        console.error('Error sending secondary course SMS:', error);
      });
    }
  }, [auth]);

  const addSecondaryCourse = useCallback(async () => {
    if (!selectedStudentDocId) {
      swal('Select a student', 'Choose the student you want to add the course to.', 'warning');
      return null;
    }

    if (!selectedSecondaryCourse) {
      swal('Select a course', 'Choose a course before adding it.', 'warning');
      return null;
    }

    if (!selectedIntakeId) {
      swal('Select an intake', 'Choose the intake you want to join.', 'warning');
      return null;
    }

    if (campusRequiresChoice && !selectedCampus) {
      swal('Select a campus', 'Choose the campus you want to attend for this intake.', 'warning');
      return null;
    }

    if (!selectedStudentRecord) {
      swal('Hold on', 'We could not load the selected student profile yet. Please try again.', 'error');
      return null;
    }

    const courseName = String(selectedSecondaryCourse || '').trim();
    const existingSecondaryNames = new Set(selectedStudentSecondaryCourses.map((course) => normalizeCourseName(course.name)));
    if (existingSecondaryNames.has(normalizeCourseName(courseName))) {
      swal('Already added', 'This course already exists for the selected student.', 'info');
      return null;
    }

    const intakeInfo = allIntakes.find((intake) => intake.id === selectedIntakeId);
    const newEntry = {
      name: courseName,
      intakeId: selectedIntakeId,
      intakeDate: intakeInfo?.intakeDate || '',
      rawIntakeDate: intakeInfo?.rawIntakeDate || '',
      campus: selectedCampus || intakeInfo?.campus || '',
      status: 'Pending',
      admissionDate: '',
      addedAt: Date.now()
    };

    setIsAddingCourse(true);

    try {
      await updateDoc(doc(db, 'users', selectedStudentDocId), {
        secondaryCourses: arrayUnion(newEntry),
        pendingSecondaryCourse: newEntry,
        updatedAt: Timestamp.now()
      });

      await sendCourseAddedNotifications(selectedStudentRecord, newEntry);

      logAdminActivity({
        action: 'Add Secondary Course',
        details: `Added ${newEntry.name} for ${selectedStudentRecord.bio || selectedStudentRecord.userName || 'student'}`,
        meta: {
          page: 'Secondary Courses',
          studentId: selectedStudentRecord.userId || selectedStudentDocId,
          studentName: selectedStudentRecord.bio || selectedStudentRecord.userName || '',
          courseName: newEntry.name,
          intakeId: newEntry.intakeId,
          campus: newEntry.campus
        }
      });

      swal('Added', 'The course has been saved as a secondary course.', 'success');
      closeAddCourseModal();
      await fetchData();
      return newEntry;
    } catch (error) {
      console.error('Error adding secondary course:', error);
      swal('Error', error?.message || 'Could not add the course. Please try again.', 'error');
      return null;
    } finally {
      setIsAddingCourse(false);
    }
  }, [
    allIntakes,
    campusRequiresChoice,
    closeAddCourseModal,
    db,
    fetchData,
    selectedCampus,
    selectedIntakeId,
    selectedSecondaryCourse,
    selectedStudentDocId,
    selectedStudentRecord,
    selectedStudentSecondaryCourses,
    sendCourseAddedNotifications
  ]);

  return (
    <div className="clientsComp applicationsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Students</p>
          <h4>Secondary Courses</h4>
          <p className="appsSubtext">Students who added a secondary course, with the intake and added date shown below.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
          <Button
            variant="dark"
            onClick={openAddCourseModal}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Course
          </Button>
          <div className="appsHeaderStats">
            <div className="appsStatChip">
              <span>Students</span>
              <strong>{uniqueStudentsCount}</strong>
            </div>
            <div className="appsStatChip">
              <span>Entries</span>
              <strong>{studentsList.length}</strong>
            </div>
            <div className="appsStatChip">
              <span>Courses</span>
              <strong>{uniqueSecondaryCourses}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard">
        <div className="appsSearch input-group">
          <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
          <Form.Control
            type="text"
            placeholder="Search student, course, intake, campus..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="appsFilterGroup">
          <button type="button" className="btn btn-outline-dark" onClick={handleRefresh} disabled={refreshing || loading}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="appsTableCard">
        <div className="appsTableHeader">
          <h6>Secondary Course Students</h6>
          <span>{filteredStudents.length} results</span>
        </div>
        <div className="appsTableWrap">
          <Table hover responsive className="appsTable">
            <thead>
              <tr>
                <th>Student</th>
                <th>Primary Course</th>
                <th>Secondary Course</th>
                <th>Intake</th>
                <th>Campus</th>
                <th>Status</th>
                <th>Added On</th>
                <th>Phone</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredStudents.map((student) => (
                <tr key={student.id}>
                  <td>
                    <div className="appsStudentCell">
                      <div className="appDefProfPic">
                        <img src={student.profilePic || defaulProfPic} alt="profile" />
                      </div>
                      <div>
                        <p className="appsStudentName">{student.bio || 'Unnamed student'}</p>
                        <span className="appsStudentMeta">{student.userEmail || student.userId || 'Student profile'}</span>
                      </div>
                    </div>
                  </td>
                  <td>{student.course || '—'}</td>
                  <td>
                    <div className="appsStudentCell">
                      <div>
                        <p className="appsStudentName">{student.secondaryCourse?.name || 'Secondary course'}</p>
                        <span className="appsStudentMeta">{student.secondaryCourse?.status || 'Pending'}</span>
                      </div>
                    </div>
                  </td>
                  <td>{student.secondaryCourse?.intakeDate || student.secondaryCourse?.rawIntakeDate || student.secondaryCourse?.intakeId || '—'}</td>
                  <td>{student.secondaryCourse?.campus || '—'}</td>
                  <td>{student.userStatus || '—'}</td>
                  <td>{formatDateTime(student.secondaryCourse?.addedAt || student.secondaryCourse?.admissionDate || student.appDate)}</td>
                  <td>
                    <MaskedPhone
                      phoneNumber={student.phoneNumber}
                      studentId={student.userId}
                      studentName={student.bio}
                    />
                  </td>
                  <td>
                    <button className="appsViewBtn" onClick={() => viewStudent(student)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {loading && (
                <tr>
                  <td colSpan="9" className="appsEmptyState">
                    Loading secondary course students...
                  </td>
                </tr>
              )}
              {!loading && filteredStudents.length === 0 && (
                <tr>
                  <td colSpan="9" className="appsEmptyState">
                    No students with secondary courses match your search yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={showAddCourseModal} onHide={closeAddCourseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Secondary Course</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Add one secondary course at a time to a student, using the same intake flow as the student portal.</p>

          <div className="row g-3">
            <div className="col-12">
              <Form.Group>
                <Form.Label>Search student</Form.Label>
                <Form.Control
                  type="text"
                  value={studentSearchTerm}
                  onChange={(event) => setStudentSearchTerm(event.target.value)}
                  placeholder="Type a name, course, phone, status, or email"
                  autoFocus
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <div className="border rounded overflow-hidden bg-white" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {!studentSearchTerm.trim() && (
                  <div className="p-3 text-muted">
                    Start typing to search across every student status.
                  </div>
                )}

                {studentSearchTerm.trim() && loadingStudentOptions && (
                  <div className="d-flex align-items-center gap-2 p-3 text-muted">
                    <Spinner animation="border" size="sm" />
                    <span>Loading students...</span>
                  </div>
                )}

                {studentSearchTerm.trim() && !loadingStudentOptions && filteredStudentOptions.length === 0 && (
                  <div className="p-3 text-danger">
                    No students match your search.
                  </div>
                )}

                {filteredStudentOptions.map((student) => {
                  const isSelected = selectedStudentDocId === student.studentDocId;
                  return (
                    <button
                      key={student.studentDocId}
                      type="button"
                      onClick={() => setSelectedStudentDocId(student.studentDocId)}
                      className="w-100 border-0 border-bottom text-start px-3 py-3"
                      style={{ outline: 'none', cursor: 'pointer', backgroundColor: isSelected ? '#e7f1ff' : '#fff' }}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div className="flex-grow-1">
                          <div className="fw-semibold text-dark">{student.bio || student.userEmail || student.userId || 'Student'}</div>
                          <div className="small text-muted">
                            <span className="me-2">{student.course || 'No course'}</span>
                            <span className="me-2">{student.userEmail || 'No email'}</span>
                            <span>{student.phoneNumber || 'No phone'}</span>
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-2">
                          <span className="badge bg-dark">{student.userStatus || 'Unknown'}</span>
                          <span className="small text-muted">{student.userId || student.studentDocId}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Secondary course</Form.Label>
                <Form.Select
                  value={selectedSecondaryCourse}
                  onChange={(event) => setSelectedSecondaryCourse(event.target.value)}
                  disabled={!selectedStudentRecord || loadingStudentRecord}
                >
                  <option value="">
                    {!selectedStudentRecord ? 'Choose a student first' : 'Select one course'}
                  </option>
                  {availableSecondaryCourses.map((course) => (
                    <option key={course.id} value={course.name}>
                      {course.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Intake</Form.Label>
                <Form.Select
                  value={selectedIntakeId}
                  onChange={(event) => setSelectedIntakeId(event.target.value)}
                  disabled={!selectedSecondaryCourse || loadingStudentRecord}
                >
                  <option value="">{selectedSecondaryCourse ? 'Select an intake' : 'Choose a course first'}</option>
                  {availableIntakes.map((intake) => (
                    <option key={intake.id} value={intake.id}>
                      {formatIntakeLabel(intake)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Campus</Form.Label>
                <Form.Select
                  value={selectedCampus}
                  onChange={(event) => setSelectedCampus(event.target.value)}
                  disabled={!selectedIntakeId || loadingStudentRecord || (availableCampusOptions.length === 1 && !campusRequiresChoice)}
                >
                  <option value="">{selectedIntakeId ? 'Select a campus' : 'Choose an intake first'}</option>
                  {availableCampusOptions.map((campus) => (
                    <option key={campus} value={campus}>
                      {campus}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <div className="mt-3 p-3 bg-light rounded">
            <div className="d-flex flex-wrap gap-3">
              <div>
                <small className="text-muted d-block">Primary course</small>
                <strong>{selectedStudentRecord?.course || '—'}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Current secondary courses</small>
                <strong>{selectedStudentSecondaryCourses.length}</strong>
              </div>
            </div>
            {selectedStudentSecondaryCourses.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-3">
                {selectedStudentSecondaryCourses.map((course) => (
                  <span key={`${course.name}-${course.intakeId}-${course.rawIntakeDate}`} className="addCourseChip">
                    {course.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {loadingStudentRecord && (
            <div className="d-flex align-items-center gap-2 mt-3">
              <Spinner animation="border" size="sm" />
              <span>Loading student profile...</span>
            </div>
          )}

          {!loadingStudentRecord && selectedStudentDocId && availableSecondaryCourses.length === 0 && (
            <p className="text-danger mt-3 mb-0">This student already has all available secondary courses.</p>
          )}

          {!loadingStudentRecord && selectedSecondaryCourse && availableIntakes.length === 0 && (
            <p className="text-danger mt-3 mb-0">No intakes are available for the selected course yet.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddCourseModal} disabled={isAddingCourse}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={addSecondaryCourse}
            disabled={
              isAddingCourse
              || loadingStudentRecord
              || !selectedStudentDocId
              || !selectedSecondaryCourse
              || !selectedIntakeId
              || (campusRequiresChoice && !selectedCampus)
            }
          >
            {isAddingCourse ? 'Adding...' : 'Add Course'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SecondaryCoursesComp;
